import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Check,
  File,
  HelpCircle,
  List,
  MessageCircleQuestion,
  Search,
  Sun,
  UserSearch,
  X,
  type LucideIcon,
} from 'lucide-react';
import { OnboardingIllustration } from './OnboardingIllustration';
import { esRutaCreacionEmpresa } from '../../utils/authRedirect';
import { useContextoTema } from '../../contexts/ContextoTema';
import { Button } from '../../contasis/components/Button/Button';

type StepId = 1 | 2;

interface Feature {
  icon: LucideIcon;
  text: string;
}

interface StepConfig {
  caption: string;
  nextLabel: string;
  title: string;
  description: string;
  features: Feature[];
  chips?: string[];
  footnoteIcon: LucideIcon;
  footnote: React.ReactNode;
}

const STEPS: Record<StepId, StepConfig> = {
  1: {
    caption: 'Tú haces la venta. Negocios Online se encarga del comprobante.',
    nextLabel: 'Continuar',
    title: 'Empieza a vender sin complicaciones',
    description:
      'Emite comprobantes válidos ante SUNAT y gestiona tus ventas, productos, clientes y cobros desde un solo lugar.',
    features: [
      { icon: File, text: 'Emite boletas y facturas sin complicarte' },
      { icon: UserSearch, text: 'Ingresa el RUC o DNI y obtén sus datos automáticamente' },
      { icon: BarChart3, text: 'Lleva el control de tus ventas, productos y cobros desde un solo lugar' },
    ],
    footnoteIcon: List,
    footnote: (
      <>
        Tu establecimiento, almacén, series y caja <strong className="font-semibold text-primary">ya están creados para ti</strong>
      </>
    ),
  },
  2: {
    caption: 'No necesitas saber de facturación. El sistema te va indicando qué hacer.',
    nextLabel: 'Crear mi empresa',
    title: 'Empieza tranquilo, nosotros te guiamos',
    description:
      'Si tienes dudas, encuéntralas en el Centro de ayuda. El tour guiado te muestra paso a paso qué debes ir configurando.',
    features: [
      { icon: Sun, text: 'Ayudas guiadas que se activan solas en cada módulo' },
      { icon: HelpCircle, text: 'Indicaciones y recomendaciones en el momento justo' },
      { icon: MessageCircleQuestion, text: 'Respuestas paso a paso a las consultas más frecuentes' },
    ],
    chips: ['Emitir una factura', 'Registrar un cliente', 'Crear un producto'],
    footnoteIcon: Check,
    footnote: (
      <>
        El <strong className="font-semibold text-primary">Centro de ayuda</strong> vive en la barra superior, listo cuando lo
        necesites. Desde ahí controlas si las ayudas guiadas están activas.
      </>
    ),
  },
};

const TOTAL_STEPS = Object.keys(STEPS).length as StepId;

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegister?: () => void;
  /** true para usuarios creados vía API key: su empresa ya existe, así que el último paso los lleva a emitir su primer comprobante en vez de crear una empresa */
  esCreadoPorApiKey?: boolean;
  onFinalizar?: () => void;
}

export function OnboardingModal({ isOpen, onClose, onRegister, esCreadoPorApiKey, onFinalizar }: OnboardingModalProps) {
  const [step, setStep] = useState<StepId>(1);
  const { tema } = useContextoTema();
  const logoSrc = '/Negocios%20Online%20-%20Logo.png';
  const location = useLocation();
  const enFlujoCreacionEmpresaConPlan =
    esRutaCreacionEmpresa(location.pathname) && new URLSearchParams(location.search).has('planSku');

  // Reinicia al paso 1 cada vez que el modal se abre.
  useEffect(() => {
    if (isOpen) setStep(1);
  }, [isOpen]);

  // Cierre con la tecla Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const current = STEPS[step];
  const isLastStep = step === TOTAL_STEPS;
  const nextLabel = isLastStep && esCreadoPorApiKey ? 'Finalizar' : current.nextLabel;

  const handleNext = () => {
    if (!isLastStep) {
      setStep((prev) => (prev + 1) as StepId);
      return;
    }
    if (esCreadoPorApiKey && onFinalizar) {
      onFinalizar();
      return;
    }
    if (enFlujoCreacionEmpresaConPlan || !onRegister) onClose();
    else onRegister();
  };

  const handleBack = () => setStep((prev) => (prev - 1) as StepId);

  return createPortal(
    <div
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="fixed inset-0 z-50 grid place-items-center bg-neutral-130/45 p-8 backdrop-blur-sm"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="grid h-auto min-h-[472px] w-full max-w-[880px] grid-cols-[320px_1fr] overflow-hidden rounded-[20px] bg-surface-0 shadow-[0_24px_64px_rgba(16,24,40,.28)] animate-fadeIn"
        style={{ maxHeight: 'calc(100vh - 48px)' }}
      >
        <OnboardingIllustration step={step} caption={current.caption} />

        <div className="flex min-h-0 min-w-0 flex-col">
          {/* Header: dots de progreso + cerrar */}
          <div className="flex flex-none items-center gap-3 px-8 pt-5">
            <div className="flex items-center gap-1.5">
              {(Object.keys(STEPS).map(Number) as StepId[]).map((dotStep) => (
                <button
                  key={dotStep}
                  type="button"
                  aria-label={`Ir al paso ${dotStep}`}
                  onClick={() => setStep(dotStep)}
                  className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out ${
                    dotStep === step ? 'w-6 bg-brand' : dotStep < step ? 'w-2 bg-[color-mix(in_srgb,var(--primary)_40%,transparent)]' : 'w-2 bg-surface-2'
                  }`}
                />
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              icon={<X size={15} />}
              iconOnly
              aria-label="Cerrar"
              onClick={onClose}
              className="ml-auto"
            />
          </div>

          {/* Contenido del paso */}
          <div className="flex-1 min-h-0 overflow-y-auto px-8 pb-1 pt-5">
            <div key={step} className="animate-fadeIn">
              {step === 1 && (
                <img
                  src={logoSrc}
                  alt="Negocios Online"
                  className={`mb-3 h-8 w-auto object-contain ${tema === 'dark' ? 'brightness-0 invert' : ''}`}
                />
              )}

              <h2 className="mb-2.5 font-display text-h3 text-primary">{current.title}</h2>
              <p className="mb-3.5 max-w-[50ch] text-body-sm text-secondary">{current.description}</p>

              <div className="flex flex-col gap-2">
                {current.features.map((feature) => (
                  <div key={feature.text} className="flex items-center gap-3.5">
                    <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-xl bg-brand-light text-brand">
                      <feature.icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                    </span>
                    <span className="text-body-sm text-secondary">{feature.text}</span>
                  </div>
                ))}
              </div>

              {current.chips && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {current.chips.map((chip) => (
                    <span
                      key={chip}
                      className="inline-flex h-[27px] items-center gap-1.5 whitespace-nowrap rounded-lg border bg-surface-0 px-2.5 text-[11.5px] text-secondary"
                    >
                      <Search className="h-3 w-3 flex-none text-brand" strokeWidth={1.9} />
                      {chip}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-3.5 flex items-start gap-2.5 border-t border-muted pt-3.5">
                <current.footnoteIcon className="mt-0.5 h-4 w-4 flex-none text-brand" strokeWidth={2} />
                <span className="text-caption text-tertiary">{current.footnote}</span>
              </div>
            </div>
          </div>

          {/* Footer: navegación */}
          <div className="flex flex-none items-center gap-2 border-t border-muted px-8 py-4">
            {step > 1 && (
              <Button
                variant="secondary"
                size="md"
                icon={<ArrowLeft size={14} />}
                iconPosition="left"
                onClick={handleBack}
              >
                Atrás
              </Button>
            )}
            <Button variant="ghost" size="md" onClick={onClose} className="ml-auto">
              Saltar
            </Button>
            <Button
              variant="primary"
              size="md"
              icon={<ArrowRight size={14} />}
              iconPosition="right"
              onClick={handleNext}
            >
              {nextLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
